import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import ListingHolidayHoursEditor from '../../ListingHolidayHoursEditor';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';
import Page from '../../Page';
import Breadcrumb from '../../Breadcrumb';

/**
 * Holidays, on their own.
 *
 * Wraps the existing `ListingHolidayHoursEditor`, which saves each row as it is
 * changed — so there is no Save button here, only a way back. That is why this
 * does not use SectionEditForm: there is no patch to build.
 *
 * It exists because the whole-listing form is going away for wineries, and
 * holiday hours would otherwise be visible on the overview but impossible to
 * change — the worst of both.
 */
export default function HolidayHoursEditForm() {
  const { lid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const user = useSelector((s) => s.auth.user);
  const role = user?.role === 'admin' ? 'admin' : 'industry';

  const [holidays, setHolidays] = useState([]);

  const getHolidays = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await client.service(`${role}/holiday`).find({
        query: {
          date: { $gte: today }, active: true, $sort: { date: 1 }, $limit: 100,
        },
      });
      setHolidays(result.data || result || []);
    } catch (e) {
      console.log('could not load holidays', e);
    }
  }, [role]);

  useEffect(() => { getHolidays(); }, [getHolidays]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (listing?.id === lid) return;
      try {
        const row = await client.service(`${role}/listing`).get(lid);
        if (!cancelled) dispatch(setListing(row));
      } catch (e) {
        console.log('could not load listing', e);
      }
    })();
    return () => { cancelled = true; };
  }, [lid, listing?.id, role, dispatch]);

  if (!listing || listing.id !== lid) return null;

  return (
    <Page>
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb
            elements={[
              { name: 'Listing Detail', href: `/${role}/listings/${lid}/` },
              { name: `${listing.name}: Holiday hours` },
            ]}
          />
        </nav>
      </div>

      <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Holiday hours</h3>
          <p className="mt-1 text-sm text-gray-500">
            Closures and special hours for the holidays coming up. Visitors check
            these before a long drive.
          </p>
        </div>
        <div className="mt-5 md:col-span-2 md:mt-0">
          <ListingHolidayHoursEditor
            listingId={lid}
            servicePath={`${role}/listing-holiday-hours`}
            holidayServicePath={`${role}/holiday`}
            holidays={holidays}
            onHolidaysChanged={getHolidays}
          />
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-gray-200 flex justify-end">
        {/* Each row saves as it changes, so there is nothing to submit. */}
        <button
          type="button"
          onClick={() => navigate(`/${role}/listings/${lid}`)}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Done
        </button>
      </div>
      </div>
    </Page>
  );
}
